import { NavigationStart, Router, RoutesRecognized } from '@angular/router';

import { DefaultHomePageCode } from 'src/constants/default-home-page.enum';
import { EntryService } from './entry.service';
import { Injectable } from '@angular/core';
import { PermissionService } from './permission.service';
import { RouteHistoryObject } from 'src/interfaces/utility-interfaces';
import { StorageService } from './storage.service';
import { environment } from 'src/environments/environment';
import { filter } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  static reportingUrl = '/reports';
  static patientSearchUrl = '/patient/search';
  static dashboardUrl = '/dashboard';
  public static manageDocumentLink = '/administration/support_document';
  public static manageNotificationLink = '/administration/system_notification';

  historyIndex: number;
  history = [] as RouteHistoryObject[];

  constructor(
    private router: Router,
    private permissionService: PermissionService,
    private oauthService: OAuthService,
    private entryService: EntryService,
    private storageService: StorageService
    ) {
      this.router.events
      .pipe(filter(event => event instanceof RoutesRecognized))
      .subscribe((e: any) => {
        // #2874,User hasn't select a profile but direct paste a link
        if (sessionStorage.getItem('access_token') && !e.url.includes('access_token')
          && e.url !== '' && e.url !== '/' && e.url !== '/app/logout' && !sessionStorage.getItem(StorageService.keyUserOrgId)) {
          this.router.navigateByUrl('');
          return;
        }

        this.checkPermission(e.url).then (hasPermission => {
          if (!hasPermission) {
            this.navigateToPatientsPage();
          } else {
            this.historyIndex = e.id;
            this.history = [...this.history, {
              id: e.id,
              url: e.url,
              urlAfterRedirects: e.urlAfterRedirects // url navigated to after navigation
            }];
            if (this.history.length > 1) {
              const previousUrl = this.history[this.history.length - 2].urlAfterRedirects as string;
              // #2966 WLE page get previous page early than navigation service set history
              storageService.setStorageItem({ key: StorageService.keyPreviousUrl, value: previousUrl });
            }
            if (e instanceof NavigationStart) {
              const browserRefresh = !router.navigated;
              if (browserRefresh && this.historyIndex === 1) {
                this.getPreviousUrl(browserRefresh);
              }
            }
          }
        });
      });
  }

  async checkPermission(targetUrl: string): Promise<boolean> {
    if (!targetUrl) { return true; }
    if (targetUrl?.indexOf(NavigationService.manageNotificationLink) >= 0 &&
    !this.permissionService.iHavePermission(PermissionService.Admin_Manage_Notification_Permission)) {
      return false;
    }
    if (targetUrl?.indexOf(NavigationService.manageDocumentLink) >= 0 &&
    !this.permissionService.iHavePermission(PermissionService.Admin_Manage_Documents_Permission)) {
      return false;
    }
    return true;
  }

  getRoutingHistory() {
      return this.history;
    }

  getPreviousUrl(browserRefresh?: boolean) {
    let previousUrl: string;
    if (browserRefresh || this.historyIndex === 1) {
      previousUrl = sessionStorage.getItem('previousUrl');
    } else if (this.historyIndex < 2) {
      previousUrl = null;
    } else if (this.historyIndex > 1) {
      const previousHistory = this.history[this.history.length - 2];
      previousUrl = previousHistory?.urlAfterRedirects;
    }
    return previousUrl;
  }

  revertPreviousUrl() {
    if (this.historyIndex > 1) {
      this.history.pop();
      this.historyIndex = this.historyIndex - 1;
      const previousHistory = this.history[this.history.length - 1];
      this.storageService.setStorageItem({ key: StorageService.keyPreviousUrl, value: previousHistory?.urlAfterRedirects });
    }
  }

  navigateToLoginPage() {
    this.router.navigateByUrl('/login');
  }

  navigateToLogoutPage() {
    this.router.navigateByUrl('/app/logout');
  }

  navigateToPatientsPage() {
    this.router.navigateByUrl(NavigationService.patientSearchUrl);
  }

  navigateToPatientSummaryPage(patientId?: string, ecid?: string, matchWeight?: string) {
    if (patientId) {
      this.router.navigateByUrl('/patient/summary/' + patientId);
    } else {
      this.router.navigate(['/patient/summary'], { queryParams: { eid: ecid, weight: matchWeight} });
    }
  }

  navigateToDashboardPage() {
    this.router.navigateByUrl(NavigationService.dashboardUrl);
  }

  getDefaultHomePageByCode(defaultPageCode: string) {
    if (defaultPageCode === DefaultHomePageCode.Dashboard) {
      return NavigationService.dashboardUrl;
    } else {
      return NavigationService.patientSearchUrl;
    }
  }

  navigateToFacilitiesHomePage() {
    this.router.navigateByUrl('/facilities');
  }

  navigateToAddFacilityPage() {
    this.router.navigateByUrl('/facilities/add');
  }

  navigateToFacilitySummaryPage(organizationId: string) {
    this.router.navigateByUrl('/facilities/summary/' + organizationId);
  }

  navigateToAddLocationPage(organizationId: string) {
    this.router.navigateByUrl('/facilities/' + organizationId + '/add-location');
  }

  navigateToAddEntryPage(patientId: string) {
    this.router.navigateByUrl('/entry/' + patientId + '/add');
  }

  navigateToEditEntryPage(entryId: string, patientId: string) {
    this.router.navigateByUrl('/entry/' + entryId + '/' + patientId + '/edit');
  }

  navigateToSelectProfilePage() {
    this.router.navigateByUrl('');
  }

  navigateToPage(pageUrl: string) {
    this.router.navigateByUrl(pageUrl);
    if (pageUrl !== NavigationService.reportingUrl) {
      this.router.navigateByUrl(pageUrl);
    } else {
      this.navigateToReportPath(pageUrl);
    }
  }

  navigateToReportPath(path: string) {
    location.assign(`${environment.reportingBaseUrl}${path}`);
  }

  appLogout(timeout?: boolean) {
    // #3551
    this.navigateToLoginPage(); 
    sessionStorage.clear(); 
    if(timeout) {
      sessionStorage.setItem('sessionTimedOut', 'true');
    }
    else {
      sessionStorage.removeItem('sessionTimedOut');
    }
    localStorage.removeItem('userLoggedIn');
    this.oauthService.logOut();
  }
}
